import { ValueAdapter } from './../../value.adapter';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { SapOpenHubCopySource } from '../../../pipeline/copysource/sap-open-hub.copysource';
import { string2UnicodeSingle, unicode2StrSingle } from 'app/shared/utils/string.util';
import { columnDelimiterWithoutNoneV2Options } from 'app/shared/components/fileformat/fileformat.constants';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SapOpenHubCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        excludeLastRequest: '*',
        baseRequestId: '*',
        customRfcReadTableFunctionModule: '*',
        sapDataColumnDelimiter: '*'
    }
})
export class SapOpenHubCopySourceAdapter extends CopySourceAdapter<SapOpenHubCopySource, Model.SapOpenHubSource> {
    protected override async _toResource(copySource: SapOpenHubCopySource): Promise<Model.SapOpenHubSource> {
        const result: Model.SapOpenHubSource = this.getCommonCopySourceProperties(
            copySource,
            Metamodel.modelFor(SapOpenHubCopySource).name
        );
        if (copySource.isV2) {
            result.baseRequestId = ValueAdapter.toResource(copySource, SapOpenHubCopySource.BASE_REQUEST_ID);
            result.excludeLastRequest = ValueAdapter.toResource(copySource, SapOpenHubCopySource.EXCLUDE_LAST_REQUEST);
            result.customRfcReadTableFunctionModule = ValueAdapter.toResource(
                copySource,
                SapOpenHubCopySource.CUSTOM_FUNCTION_MODULE
            );

            const sapDataColumnDelimiter = ValueAdapter.toResource(
                copySource,
                SapOpenHubCopySource.SAP_COLUMN_DELIMITER
            );
            try {
                result.sapDataColumnDelimiter = JSON.parse(
                    JSON.stringify(sapDataColumnDelimiter).replace(/\\\\/g, '\\')
                );
            } catch {
                result.sapDataColumnDelimiter = sapDataColumnDelimiter;
            }
            if (result.sapDataColumnDelimiter != null && typeof result.sapDataColumnDelimiter === 'string') {
                result.sapDataColumnDelimiter = string2UnicodeSingle(result.sapDataColumnDelimiter);
            }
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.SapOpenHubSource,
        copyActivity: CopyActivity
    ): Promise<SapOpenHubCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            SapOpenHubCopySource.MODEL,
            resource,
            copyActivity
        ) as SapOpenHubCopySource;
        result.mode = 'table'; // only support table
        if (resource.excludeLastRequest != null) {
            result.isV2 = true;
            ValueAdapter.toModel(result, SapOpenHubCopySource.EXCLUDE_LAST_REQUEST, resource.excludeLastRequest);
            ValueAdapter.toModel(result, SapOpenHubCopySource.BASE_REQUEST_ID, resource.baseRequestId);
            ValueAdapter.toModel(
                result,
                SapOpenHubCopySource.CUSTOM_FUNCTION_MODULE,
                resource.customRfcReadTableFunctionModule
            );

            ValueAdapter.toModel(result, SapOpenHubCopySource.SAP_COLUMN_DELIMITER, resource.sapDataColumnDelimiter);
            if (result.sapDataColumnDelimiter != null && typeof result.sapDataColumnDelimiter === 'string') {
                result.sapDataColumnDelimiter = unicode2StrSingle(
                    result.sapDataColumnDelimiter,
                    columnDelimiterWithoutNoneV2Options.map((option) => option.value)
                );
                if (!result.sapDataColumnDelimiter.startsWith('\\u')) {
                    // reserve \\ to avoid second escape
                    result.sapDataColumnDelimiter = result.sapDataColumnDelimiter.replace(/\\/g, '\\\\');
                }
            }
        }

        return result;
    }
}
